import React from 'react';
import { Shield, Lock, Eye, Server, CheckCircle, AlertTriangle, FileText, Users, Brain, Clock, Database, Key, Activity, Settings, ArrowRight, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Security = () => {
  const securityPillars = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All data encrypted in transit and at rest using AES-256 and TLS 1.3',
      details: [
        'Health records fully encrypted',
        'Secure chat and messaging',
        'Protected diagnostic reports',
        'AI interaction encryption'
      ],
      color: '#286CFF'
    },
    {
      icon: Shield,
      title: 'HIPAA-Ready, GDPR-Aligned Compliance',
      description: 'Designed to meet global healthcare regulations and standards',
      details: [
        'Strict access controls',
        'Complete audit trails',
        'PHI/PII protection',
        'Regulatory compliance monitoring'
      ],
      color: '#28C08A'
    },
    {
      icon: Brain,
      title: 'Secure AI & Data Governance',
      description: 'Responsible AI with strict anonymization and consent protocols',
      details: [
        'AI trained with anonymized data',
        'Consent-based data usage',
        'Responsible AI guidelines',
        'No data selling or marketing use'
      ],
      color: '#FFD28A'
    },
    {
      icon: Server,
      title: 'Multi-Layer Infrastructure Security',
      description: 'Enterprise-grade protection with continuous monitoring',
      details: [
        'Advanced firewalls',
        'Intrusion detection systems',
        'Encrypted cloud infrastructure',
        'Zero-trust architecture'
      ],
      color: '#FF6B6B'
    },
    {
      icon: Key,
      title: 'Secure Access & Identity Management',
      description: 'Multi-factor authentication and role-based access control',
      details: [
        'Multi-Factor Authentication (MFA)',
        'Role-based access control',
        'Isolated provider dashboards',
        'Secure session management'
      ],
      color: '#79A8FF'
    },
    {
      icon: Activity,
      title: 'Regular Security Testing',
      description: 'Continuous assessment and improvement of security measures',
      details: [
        'Regular penetration testing',
        'Vulnerability scanning',
        'Third-party security audits',
        'Proactive patch management'
      ],
      color: '#286CFF'
    }
  ];

  const protectionFeatures = [
    { icon: '🔐', title: 'Safe Login + MFA', desc: 'Multi-factor authentication for all accounts' },
    { icon: '💳', title: 'Secure Payments', desc: 'PCI-DSS compliant payment processing' },
    { icon: '📄', title: 'Encrypted Documents', desc: 'Medical records secured at rest and in transit' },
    { icon: '🤖', title: 'AI Advice Security', desc: 'Health Genie data stored with encryption' },
    { icon: '📞', title: 'Secure Teleconsultations', desc: 'End-to-end encrypted video calls' },
    { icon: '🏥', title: 'Tamper-Proof Records', desc: 'Surgical and diagnostic data integrity' },
    { icon: '👨‍⚕️', title: 'Controlled Provider Access', desc: 'Granular permissions for healthcare teams' },
    { icon: '💾', title: 'Continuous Backups', desc: 'Automated data backup and recovery' }
  ];

  const certifications = [
    { name: 'HIPAA', status: 'Ready', desc: 'Health Insurance Portability and Accountability Act' },
    { name: 'GDPR', status: 'Aligned', desc: 'General Data Protection Regulation' },
    { name: 'ISO 27001', status: 'Pursuing', desc: 'Information Security Management' },
    { name: 'SOC 2', status: 'Future', desc: 'Service Organization Control' }
  ];

  const privacyRights = [
    { icon: Eye, title: 'You Control Your Data', desc: 'Full control over your health information' },
    { icon: FileText, title: 'Transparent Usage', desc: 'Clear explanation of how data is used' },
    { icon: Shield, title: 'No Unauthorized Sharing', desc: 'Your data stays private and protected' },
    { icon: CheckCircle, title: 'Consent-Based Collection', desc: 'Permission required for all data usage' }
  ];

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div style={styles.container}>
          <div style={styles.heroContent}>
            <div style={styles.shieldIcon}>
              <svg viewBox="0 0 100 100" style={{ width: '80px', height: '80px' }}>
                <defs>
                  <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#28C08A', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#1E8E6F', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <path d="M50 10 L80 25 L80 50 C80 70 65 85 50 90 C35 85 20 70 20 50 L20 25 Z" 
                      fill="url(#shieldGrad)" stroke="white" strokeWidth="3"/>
                <path d="M35 50 L45 60 L65 40" stroke="white" strokeWidth="5" 
                      strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            
            <h1 style={styles.heroTitle}>
              Your Health Data is Safe, Secure & Protected—Always
            </h1>
            <p style={styles.heroSubtitle}>
              GEWO Health follows global security standards, encryption practices, and AI safety 
              protocols to keep your information private and secure
            </p>
            
            <button 
              style={styles.heroButton}
              onClick={() => document.getElementById('security-pillars').scrollIntoView({ behavior: 'smooth' })}
            >
              Security Overview <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </button>
          </div>
        </div>
        
        {/* Security Pattern Background */}
        <div style={styles.securityPattern}>
          <svg viewBox="0 0 800 400" style={{ width: '100%', height: '100%', opacity: 0.1 }}>
            {[...Array(20)].map((_, i) => (
              <circle 
                key={i}
                cx={Math.random() * 800}
                cy={Math.random() * 400}
                r={Math.random() * 30 + 10}
                fill="#28C08A"
                opacity={Math.random() * 0.5}
              />
            ))}
            {[...Array(15)].map((_, i) => (
              <rect 
                key={`rect-${i}`}
                x={Math.random() * 800}
                y={Math.random() * 400}
                width={Math.random() * 40 + 20}
                height={Math.random() * 40 + 20}
                fill="#286CFF"
                opacity={Math.random() * 0.3}
                transform={`rotate(${Math.random() * 360})`}
              />
            ))}
          </svg>
        </div>
      </section>

      {/* Introduction */}
      <section style={styles.introSection}>
        <div style={styles.container}>
          <div style={styles.introContent}>
            <h2 style={styles.sectionTitle}>Introduction to GEWO Health Security</h2>
            
            <div style={styles.introText}>
              <p style={styles.paragraph}>
                GEWO Health protects <strong>patient</strong>, <strong>provider</strong>, and 
                <strong> organizational data</strong> with enterprise-grade security, using advanced 
                encryption and compliance frameworks. Every touchpoint in our ecosystem is built with 
                security as a foundational principle.
              </p>
              
              <p style={styles.paragraph}>
                Every service—<strong>Appointments</strong>, <strong>Surgeries</strong>, 
                <strong> Specializations</strong>, <strong>AI Health Genie</strong>, 
                <strong> Elderly Care</strong>, <strong>Women's Health</strong>, and 
                <strong> Lifestyle Disease Management</strong>—is designed with 
                <em> privacy-by-default</em> and <em>security-by-design</em> principles.
              </p>
            </div>
            
            <div style={styles.missionBox}>
              <div style={styles.missionIcon}>
                <Shield size={32} color="#286CFF" />
              </div>
              <div style={styles.missionText}>
                <h3 style={styles.missionTitle}>Our Mission</h3>
                <p style={styles.missionDesc}>
                  Ensure safe, uninterrupted, and compliant healthcare experiences for every user
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Security Pillars */}
      <section style={styles.pillarsSection} id="security-pillars">
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Core Security Pillars</h2>
          <p style={styles.sectionSubtitle}>
            Six foundational layers protecting your healthcare data
          </p>
          
          <div style={styles.pillarsGrid}>
            {securityPillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div key={index} style={styles.pillarCard}>
                  <div style={{ ...styles.pillarIcon, background: `${pillar.color}20` }}>
                    <Icon size={32} color={pillar.color} />
                  </div>
                  <h3 style={styles.pillarTitle}>{pillar.title}</h3>
                  <p style={styles.pillarDescription}>{pillar.description}</p>
                  <ul style={styles.pillarDetails}>
                    {pillar.details.map((detail, idx) => (
                      <li key={idx} style={styles.pillarDetailItem}>
                        <CheckCircle size={16} color={pillar.color} style={{ marginRight: '8px', flexShrink: 0 }} />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How GEWO Health Protects You */}
      <section style={styles.protectionSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>How GEWO Health Protects You</h2>
          <p style={styles.sectionSubtitle}>
            Comprehensive security across every interaction
          </p>
          
          <div style={styles.protectionGrid}>
            {protectionFeatures.map((feature, index) => (
              <div key={index} style={styles.protectionCard}>
                <div style={styles.protectionEmoji}>{feature.icon}</div>
                <h3 style={styles.protectionTitle}>{feature.title}</h3>
                <p style={styles.protectionDesc}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Storage & Retention */}
      <section style={styles.dataSection}>
        <div style={styles.container}>
          <div style={styles.dataGrid}>
            <div style={styles.dataContent}>
              <h2 style={styles.dataTitle}>Data Storage & Retention</h2>
              <p style={styles.dataDescription}>
                Your data is stored securely and managed responsibly throughout its lifecycle
              </p>
              
              <div style={styles.dataPoints}>
                {[
                  { icon: Database, text: 'Data stored in certified cloud datacenters with SOC 2 compliance' },
                  { icon: Settings, text: 'Automatic data minimization - only essential information retained' },
                  { icon: FileText, text: 'Retention policies aligned with healthcare standards (HIPAA, GDPR)' },
                  { icon: CheckCircle, text: 'Safe deletion when requested - complete data removal' }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} style={styles.dataPoint}>
                      <Icon size={24} color="#286CFF" />
                      <span style={styles.dataPointText}>{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div style={styles.dataIllustration}>
              <svg viewBox="0 0 300 300" style={{ width: '100%', height: 'auto' }}>
                <circle cx="150" cy="150" r="120" fill="#E3EDFF" opacity="0.3"/>
                
                {/* Database stack */}
                <g transform="translate(150, 150)">
                  <ellipse cx="0" cy="-40" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                  <rect x="-50" y="-40" width="100" height="20" fill="#286CFF" opacity="0.8"/>
                  <ellipse cx="0" cy="-20" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                  
                  <ellipse cx="0" cy="0" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                  <rect x="-50" y="0" width="100" height="20" fill="#286CFF" opacity="0.8"/>
                  <ellipse cx="0" cy="20" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                  
                  <ellipse cx="0" cy="40" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                  <rect x="-50" y="40" width="100" height="20" fill="#286CFF" opacity="0.8"/>
                  <ellipse cx="0" cy="60" rx="50" ry="15" fill="#286CFF" opacity="0.9"/>
                </g>
                
                {/* Lock */}
                <g transform="translate(210, 100)">
                  <rect x="-15" y="0" width="30" height="35" rx="5" fill="#28C08A"/>
                  <path d="M-10 0 L-10 -15 Q-10 -25 0 -25 Q10 -25 10 -15 L10 0" 
                        stroke="#28C08A" strokeWidth="5" fill="none"/>
                  <circle cx="0" cy="15" r="5" fill="white"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Incident Response */}
      <section style={styles.incidentSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Incident Response & Monitoring</h2>
          <p style={styles.sectionSubtitle}>
            24/7 vigilance to protect your data and respond to threats
          </p>
          
          <div style={styles.incidentGrid}>
            <div style={styles.incidentCard}>
              <Clock size={40} color="#286CFF" />
              <h3 style={styles.incidentTitle}>24/7 Security Monitoring</h3>
              <p style={styles.incidentDesc}>
                Continuous surveillance of all systems with real-time threat detection
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <AlertTriangle size={40} color="#FFD28A" />
              <h3 style={styles.incidentTitle}>Real-Time Alerting</h3>
              <p style={styles.incidentDesc}>
                Instant notifications for any suspicious activity or security events
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <Activity size={40} color="#FF6B6B" />
              <h3 style={styles.incidentTitle}>Rapid Breach Response</h3>
              <p style={styles.incidentDesc}>
                Defined protocols for immediate containment and resolution
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <Settings size={40} color="#28C08A" />
              <h3 style={styles.incidentTitle}>Backup & Recovery</h3>
              <p style={styles.incidentDesc}>
                Automated backup systems ensure data availability and business continuity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Practices */}
      <section style={styles.privacySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Your Privacy Rights</h2>
          <p style={styles.sectionSubtitle}>
            We put you in control of your personal health information
          </p>
          
          <div style={styles.privacyGrid}>
            {privacyRights.map((right, index) => {
              const Icon = right.icon;
              return (
                <div key={index} style={styles.privacyCard}>
                  <div style={styles.privacyIcon}>
                    <Icon size={28} color="#286CFF" />
                  </div>
                  <h3 style={styles.privacyTitle}>{right.title}</h3>
                  <p style={styles.privacyDesc}>{right.desc}</p>
                </div>
              );
            })}
          </div>
          
          <div style={styles.privacyRights}>
            <h3 style={styles.privacyRightsTitle}>Your Rights Include:</h3>
            <ul style={styles.privacyRightsList}>
              <li><strong>Access:</strong> View all data we hold about you</li>
              <li><strong>Modify:</strong> Update or correct your information</li>
              <li><strong>Export:</strong> Download your data in standard formats</li>
              <li><strong>Delete:</strong> Request permanent deletion of your data</li>
              <li><strong>Restrict:</strong> Limit how your data is processed</li>
              <li><strong>Object:</strong> Opt out of specific data uses</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Security for Providers & Organizations */}
      <section style={styles.providerSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Security for Providers & Organizations</h2>
          <p style={styles.sectionSubtitle}>
            Enterprise-grade protection for healthcare professionals and institutions
          </p>
          
          <div style={styles.providerFeatures}>
            <div style={styles.providerFeature}>
              <div style={styles.providerIcon}>
                <Users size={32} color="#286CFF" />
              </div>
              <div style={styles.providerContent}>
                <h3 style={styles.providerFeatureTitle}>Secure Dashboards</h3>
                <p style={styles.providerFeatureDesc}>
                  Role-based access with isolated environments for each provider and organization
                </p>
              </div>
            </div>
            
            <div style={styles.providerFeature}>
              <div style={styles.providerIcon}>
                <FileText size={32} color="#28C08A" />
              </div>
              <div style={styles.providerContent}>
                <h3 style={styles.providerFeatureTitle}>Controlled Patient File Access</h3>
                <p style={styles.providerFeatureDesc}>
                  Granular permissions ensure providers only access authorized patient records
                </p>
              </div>
            </div>
            
            <div style={styles.providerFeature}>
              <div style={styles.providerIcon}>
                <Activity size={32} color="#FFD28A" />
              </div>
              <div style={styles.providerContent}>
                <h3 style={styles.providerFeatureTitle}>Encrypted Analytics</h3>
                <p style={styles.providerFeatureDesc}>
                  Health insights and analytics protected with end-to-end encryption
                </p>
              </div>
            </div>
            
            <div style={styles.providerFeature}>
              <div style={styles.providerIcon}>
                <Lock size={32} color="#FF6B6B" />
              </div>
              <div style={styles.providerContent}>
                <h3 style={styles.providerFeatureTitle}>Safe Clinical Notes Storage</h3>
                <p style={styles.providerFeatureDesc}>
                  All clinical documentation encrypted and access-logged for audit trails
                </p>
              </div>
            </div>
            
            <div style={styles.providerFeature}>
              <div style={styles.providerIcon}>
                <Brain size={32} color="#79A8FF" />
              </div>
              <div style={styles.providerContent}>
                <h3 style={styles.providerFeatureTitle}>Protected AI Risk Prediction</h3>
                <p style={styles.providerFeatureDesc}>
                  AI-generated insights secured with strict multi-layer security protocols
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section style={styles.certSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Certifications & Compliance</h2>
          <p style={styles.sectionSubtitle}>
            Committed to meeting and exceeding global healthcare security standards
          </p>
          
          <div style={styles.certGrid}>
            {certifications.map((cert, index) => (
              <div key={index} style={styles.certCard}>
                <div style={styles.certBadge}>
                  <Shield size={40} color="#286CFF" />
                </div>
                <h3 style={styles.certName}>{cert.name}</h3>
                <div style={styles.certStatus}>{cert.status}</div>
                <p style={styles.certDesc}>{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible AI */}
      <section style={styles.aiSection}>
        <div style={styles.container}>
          <div style={styles.aiContent}>
            <div style={styles.aiHeader}>
              <Brain size={48} color="#286CFF" />
              <h2 style={styles.aiTitle}>Responsible AI at GEWO Health</h2>
            </div>
            
            <p style={styles.aiDescription}>
              Our AI systems, including the Health Genie, are built with safety, transparency, and 
              ethical considerations at their core
            </p>
            
            <div style={styles.aiPrinciples}>
              <div style={styles.aiPrinciple}>
                <CheckCircle size={24} color="#28C08A" />
                <div>
                  <h3 style={styles.aiPrincipleTitle}>AI Transparency</h3>
                  <p style={styles.aiPrincipleDesc}>
                    Clear disclosure when AI is involved in recommendations or decisions
                  </p>
                </div>
              </div>
              
              <div style={styles.aiPrinciple}>
                <CheckCircle size={24} color="#28C08A" />
                <div>
                  <h3 style={styles.aiPrincipleTitle}>Human Oversight</h3>
                  <p style={styles.aiPrincipleDesc}>
                    No critical decisions made without user or doctor oversight and approval
                  </p>
                </div>
              </div>
              
              <div style={styles.aiPrinciple}>
                <CheckCircle size={24} color="#28C08A" />
                <div>
                  <h3 style={styles.aiPrincipleTitle}>Safety Protocols</h3>
                  <p style={styles.aiPrincipleDesc}>
                    Health Genie operates within defined safety boundaries with continuous monitoring
                  </p>
                </div>
              </div>
              
              <div style={styles.aiPrinciple}>
                <CheckCircle size={24} color="#28C08A" />
                <div>
                  <h3 style={styles.aiPrincipleTitle}>Bias Testing</h3>
                  <p style={styles.aiPrincipleDesc}>
                    Regular evaluation and mitigation of algorithmic bias across demographics
                  </p>
                </div>
              </div>
              
              <div style={styles.aiPrinciple}>
                <CheckCircle size={24} color="#28C08A" />
                <div>
                  <h3 style={styles.aiPrincipleTitle}>Ethical Algorithms</h3>
                  <p style={styles.aiPrincipleDesc}>
                    AI models designed following medical ethics and healthcare best practices
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <div style={styles.ctaContent}>
            <Shield size={64} color="white" style={{ marginBottom: '24px' }} />
            <h2 style={styles.ctaTitle}>
              Security is our commitment. Your trust is our responsibility.
            </h2>
            <p style={styles.ctaSubtitle}>
              We're here to answer your security questions and address any concerns
            </p>
            
            <div style={styles.ctaButtons}>
              <button style={styles.ctaButton}>
                <FileText size={20} style={{ marginRight: '8px' }} />
                View Privacy Policy
              </button>
              <button style={styles.ctaButtonSecondary}>
                <Users size={20} style={{ marginRight: '8px' }} />
                Contact Security Team
              </button>
              <button style={styles.ctaButtonOutline}>
                <AlertTriangle size={20} style={{ marginRight: '8px' }} />
                Report a Security Concern
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    paddingTop: '120px',
    paddingBottom: '80px',
    position: 'relative',
    overflow: 'hidden',
    color: 'white'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1
  },
  shieldIcon: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center'
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '20px'
  },
  heroSubtitle: {
    fontSize: '20px',
    lineHeight: '1.6',
    marginBottom: '32px',
    opacity: 0.95
  },
  heroButton: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s ease'
  },
  securityPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  introSection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  introContent: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '40px',
    fontWeight: '700',
    color: '#2D3748',
    textAlign: 'center',
    marginBottom: '16px'
  },
  introText: {
    marginTop: '40px',
    marginBottom: '40px'
  },
  paragraph: {
    fontSize: '17px',
    color: '#2D3748',
    lineHeight: '1.8',
    marginBottom: '20px'
  },
  missionBox: {
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
    borderRadius: '16px',
    padding: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px'
  },
  missionIcon: {
    width: '64px',
    height: '64px',
    background: 'white',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  missionText: {},
  missionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#286CFF',
    marginBottom: '8px'
  },
  missionDesc: {
    fontSize: '16px',
    color: '#2D3748',
    lineHeight: '1.6'
  },
  pillarsSection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: '60px'
  },
  pillarsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px'
  },
  pillarCard: {
    background: '#F8FAFC',
    borderRadius: '20px',
    padding: '32px 24px',
    transition: 'all 0.3s ease'
  },
  pillarIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  pillarTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '12px'
  },
  pillarDescription: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.6',
    marginBottom: '20px'
  },
  pillarDetails: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  pillarDetailItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '13px',
    color: '#2D3748',
    lineHeight: '1.5'
  },
  protectionSection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  protectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  protectionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    border: '1px solid #E2E8F0',
    transition: 'all 0.3s ease'
  },
  protectionEmoji: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  protectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  protectionDesc: {
    fontSize: '13px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  dataSection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  dataGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center'
  },
  dataContent: {},
  dataTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '16px'
  },
  dataDescription: {
    fontSize: '16px',
    color: '#64748B',
    lineHeight: '1.6',
    marginBottom: '32px'
  },
  dataPoints: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  dataPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px'
  },
  dataPointText: {
    fontSize: '15px',
    color: '#2D3748',
    lineHeight: '1.6'
  },
  dataIllustration: {
    display: 'flex',
    justifyContent: 'center'
  },
  incidentSection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  incidentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  incidentCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px 24px',
    textAlign: 'center'
  },
  incidentTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2D3748',
    margin: '16px 0 12px'
  },
  incidentDesc: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  privacySection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  privacyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
    marginBottom: '60px'
  },
  privacyCard: {
    background: '#F8FAFC',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center'
  },
  privacyIcon: {
    width: '56px',
    height: '56px',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  privacyTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  privacyDesc: {
    fontSize: '13px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  privacyRights: {
    background: '#E3EDFF',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '800px',
    margin: '0 auto'
  },
  privacyRightsTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#286CFF',
    marginBottom: '16px'
  },
  privacyRightsList: {
    fontSize: '15px',
    color: '#2D3748',
    lineHeight: '1.8',
    paddingLeft: '24px'
  },
  providerSection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  providerFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  providerFeature: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px'
  },
  providerIcon: {
    width: '64px',
    height: '64px',
    background: '#E3EDFF',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  providerContent: {
    flex: 1
  },
  providerFeatureTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  providerFeatureDesc: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.6'
  },
  certSection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  certGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  certCard: {
    background: '#F8FAFC',
    borderRadius: '16px',
    padding: '32px 24px',
    textAlign: 'center',
    border: '2px solid #E2E8F0'
  },
  certBadge: {
    marginBottom: '16px'
  },
  certName: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  certStatus: {
    display: 'inline-block',
    background: '#E3EDFF',
    color: '#286CFF',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '12px'
  },
  certDesc: {
    fontSize: '13px',
    color: '#64748B'
  },
  aiSection: {
    padding: '80px 0',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #E3EDFF 100%)'
  },
  aiContent: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  aiHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px'
  },
  aiTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#2D3748'
  },
  aiDescription: {
    fontSize: '17px',
    color: '#2D3748',
    lineHeight: '1.8',
    textAlign: 'center',
    marginBottom: '40px'
  },
  aiPrinciples: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  aiPrinciple: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    background: 'white',
    borderRadius: '12px',
    padding: '20px'
  },
  aiPrincipleTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  aiPrincipleDesc: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.6'
  },
  ctaSection: {
    padding: '80px 0',
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    color: 'white'
  },
  ctaContent: {
    textAlign: 'center'
  },
  ctaTitle: {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '16px'
  },
  ctaSubtitle: {
    fontSize: '18px',
    marginBottom: '40px',
    opacity: 0.95
  },
  ctaButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  ctaButton: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 28px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
  },
  ctaButtonSecondary: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 28px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center'
  },
  ctaButtonOutline: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: '16px 28px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center'
  }
};

export default Security;
